import { CommonModule, Location } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { MessageService, TreeNode } from 'primeng/api';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { TreeTableModule } from 'primeng/treetable';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { PageDetailHeroComponent } from '../../shared/page-layout/page-detail-hero.component';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { ChartOfAccounts } from '../chart-of-accounts.model';
import { ChartOfAccountsService } from '../chart-of-accounts.service';

@Component({
    selector: 'app-chart-of-accounts-detail',
    standalone: true,
    imports: [
        CommonModule,
        ButtonModule,
        TableModule,
        TagModule,
        ToastModule,
        TreeTableModule,
        PageShellComponent,
        PageDetailHeroComponent,
        PageDataPanelComponent
    ],
    providers: [ChartOfAccountsService, MessageService],
    template: `
        <app-page-shell>
            <p-toast position="bottom-center"></p-toast>

            <app-page-detail-hero *ngIf="account" eyebrow="Chart of Accounts" [title]="account.accountName"
                [subtitle]="account.accountCode" icon="pi pi-sitemap">
                <div tags>
                    <p-tag [value]="$any(account).accountType?.accountTypeName || 'Account'" severity="info"></p-tag>
                    <p-tag [value]="account.internalType || 'No internal type'" [severity]="account.internalType ? 'secondary' : 'warn'"></p-tag>
                    <p-tag [value]="accountStatus()" [severity]="accountStatusSeverity()"></p-tag>
                    <p-tag [value]="formatBalanceLabel(account)" [severity]="isCreditBalance(account) ? 'warn' : 'success'"></p-tag>
                    <p-tag *ngIf="isProtectedPostingAccount()" value="Posting dependency" severity="warn"></p-tag>
                </div>
                <div actions>
                    <p-button label="Back" icon="pi pi-arrow-left" severity="secondary" [outlined]="true" (onClick)="back()"></p-button>
                    <p-button label="Add Child" icon="pi pi-plus" severity="success" [outlined]="true" (onClick)="addChild()"></p-button>
                    <p-button label="Edit" icon="pi pi-pencil" severity="secondary" [outlined]="true" *ngIf="account.allowEditing" (onClick)="edit()"></p-button>
                </div>
            </app-page-detail-hero>

            <app-page-data-panel *ngIf="account && isProtectedPostingAccount()" title="Posting Compatibility"
                subtitle="This account name is currently used by accounting posting automation.">
                <div class="warning-row">
                    <i class="pi pi-exclamation-triangle"></i>
                    <span>Do not rename, deactivate, delete, or repurpose this account until posting logic is moved from exact account-name lookup to configured account mappings.</span>
                </div>
            </app-page-data-panel>

            <app-page-data-panel *ngIf="account" title="Account Profile" subtitle="Master data and posting controls.">
                <div class="detail-grid">
                    <div><strong>Account ID</strong><span>{{ account.accountId }}</span></div>
                    <div><strong>Code</strong><span>{{ account.accountCode }}</span></div>
                    <div><strong>Name</strong><span>{{ account.accountName }}</span></div>
                    <div><strong>Type</strong><span>{{ $any(account).accountType?.accountTypeName || '-' }}</span></div>
                    <div><strong>Category</strong><span>{{ $any(account).accountType?.accountCategory || '-' }}</span></div>
                    <div><strong>Subcategory</strong><span>{{ $any(account).accountType?.accountSubCategory || '-' }}</span></div>
                    <div class="balance-card"><strong>Current Balance</strong><span>{{ formatBalance(account) }}</span><small>{{ balanceSide(account) }}</small></div>
                    <div><strong>Internal Type</strong><span>{{ account.internalType || '-' }}</span></div>
                    <div><strong>Currency</strong><span>{{ account.currencyCode || '-' }}</span></div>
                    <div><strong>Active</strong><span>{{ account.isActive === false ? 'No' : 'Yes' }}</span></div>
                    <div><strong>Deprecated</strong><span>{{ account.deprecated ? 'Yes' : 'No' }}</span></div>
                    <div><strong>Reconciliation</strong><span>{{ account.allowReconciliation ? 'Enabled' : 'Off' }}</span></div>
                    <div><strong>Editable</strong><span>{{ account.allowEditing ? 'Yes' : 'No' }}</span></div>
                    <div><strong>Parent</strong><span>{{ account.parentAccount?.accountCode || '-' }} {{ account.parentAccount?.accountName || '' }}</span></div>
                    <div><strong>Children</strong><span>{{ account.childrenAccounts?.length || 0 }}</span></div>
                </div>
            </app-page-data-panel>

            <app-page-data-panel *ngIf="account" title="Child Account Tree" [resultText]="childAccountCount() + ' accounts'">
                <p-treeTable [value]="childAccountTree" dataKey="accountId" styleClass="detail-coa-tree"
                    [tableStyle]="{ 'min-width': '58rem' }">
                    <ng-template pTemplate="header">
                        <tr>
                            <th>Account</th>
                            <th class="balance-col">Balance</th>
                            <th>Details</th>
                            <th>Status</th>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-rowNode let-rowData="rowData">
                        <tr [ttRow]="rowNode">
                            <td>
                                <div class="tree-account-cell">
                                    <p-treeTableToggler [rowNode]="rowNode"></p-treeTableToggler>
                                    <div class="account-copy">
                                        <button type="button" class="account-link" (click)="viewChild(rowData)">
                                            {{ rowData.accountName }}
                                        </button>
                                        <span class="account-code">{{ rowData.accountCode }}</span>
                                    </div>
                                </div>
                            </td>
                            <td class="balance-col">
                                <div class="balance-stack" [class.credit]="isCreditBalance(rowData)"
                                    [class.zero]="isZeroBalance(rowData)">
                                    <span>{{ formatBalance(rowData) }}</span>
                                    <small>{{ balanceSide(rowData) }}</small>
                                </div>
                            </td>
                            <td>
                                <div class="details-stack">
                                    <strong>{{ rowData.accountType?.accountTypeName || '-' }}</strong>
                                    <span>{{ rowData.internalType || '-' }} · {{ rowData.currencyCode || '-' }}</span>
                                    <small *ngIf="rowData.childrenAccounts?.length">{{ rowData.childrenAccounts.length }} child accounts</small>
                                </div>
                            </td>
                            <td><p-tag [value]="accountStatusFor(rowData)" [severity]="accountStatusSeverityFor(rowData)"></p-tag></td>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="emptymessage">
                        <tr>
                            <td colspan="4" class="empty-cell">No child accounts are configured.</td>
                        </tr>
                    </ng-template>
                </p-treeTable>
            </app-page-data-panel>
        </app-page-shell>
    `,
    styles: [`
        .detail-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
            gap: .75rem;
        }

        .detail-grid div {
            display: flex;
            flex-direction: column;
            gap: .25rem;
            min-width: 0;
            padding: .75rem;
            border: 1px solid #e2e8f0;
            border-radius: 8px;
            background: #ffffff;
        }

        .detail-grid span {
            color: #475569;
            overflow-wrap: anywhere;
        }

        .detail-grid small {
            color: #64748b;
            font-size: .76rem;
            font-weight: 700;
            text-transform: uppercase;
        }

        .balance-card span {
            color: #0f766e;
            font-size: 1rem;
            font-weight: 850;
        }

        .warning-row {
            display: flex;
            align-items: flex-start;
            gap: .75rem;
            color: #92400e;
            font-weight: 700;
        }

        .warning-row i {
            margin-top: .15rem;
        }

        .empty-cell {
            color: #64748b;
            padding: 1rem;
            text-align: center;
        }

        .tree-account-cell {
            display: flex;
            align-items: center;
            gap: .55rem;
            min-width: 0;
        }

        .account-copy,
        .details-stack,
        .balance-stack {
            display: grid;
            min-width: 0;
        }

        .account-copy {
            gap: .2rem;
        }

        .account-link {
            width: fit-content;
            max-width: 100%;
            border: 0;
            padding: 0;
            color: #1e2f4f;
            background: transparent;
            cursor: pointer;
            font: inherit;
            font-weight: 800;
            text-align: left;
        }

        .account-link:hover {
            color: #087a7a;
            text-decoration: underline;
        }

        .account-code {
            color: #087a7a;
            font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
            font-size: .8rem;
            font-weight: 800;
        }

        .balance-col {
            width: 13rem;
            text-align: right;
        }

        .balance-stack {
            justify-items: end;
            color: #0f766e;
            font-weight: 850;
        }

        .balance-stack.credit {
            color: #b45309;
        }

        .balance-stack.zero {
            color: #64748b;
        }

        .balance-stack small,
        .details-stack small,
        .details-stack span {
            color: #64748b;
            font-size: .78rem;
            font-weight: 650;
        }

        .details-stack {
            gap: .18rem;
        }

        .details-stack strong {
            color: #1e2f4f;
        }

        :host ::ng-deep .detail-coa-tree .p-treetable-thead > tr > th {
            border: 0;
            border-bottom: 1px solid #dbe5ee;
            color: #334155;
            background: #f8fafc;
            font-size: .8rem;
            font-weight: 850;
        }

        :host ::ng-deep .detail-coa-tree .p-treetable-tbody > tr > td {
            border: 0;
            border-bottom: 1px solid #e6edf4;
            padding: .78rem 1rem;
            vertical-align: middle;
        }

        :host ::ng-deep .detail-coa-tree .p-treetable-tbody > tr:hover > td {
            background: #f8ffff;
        }

        :host ::ng-deep .detail-coa-tree .p-treetable-toggler {
            flex: 0 0 auto;
            width: 2rem;
            height: 2rem;
            border: 1px solid #dbe5ee;
            border-radius: 8px;
            color: #64748b;
            background: #ffffff;
        }
    `]
})
export class ChartOfAccountsDetailComponent implements OnInit {
    account?: ChartOfAccounts;
    childAccountTree: TreeNode<ChartOfAccounts>[] = [];

    private readonly protectedPostingAccountNames = new Set([
        'Sales',
        'Trade Accounts Receivable',
        'Sales Tax Payable',
        'Cash and Cash Equivalents',
        'Trade Accounts Payable',
        'Driver Advances',
        'Employee Advances',
        'Expenses',
        'Input VAT Account',
        'Driver Trip Expenses',
        'Workforce Cash Variance',
        'Workforce Cash Return'
    ]);

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private location: Location,
        private chartOfAccountsService: ChartOfAccountsService,
        private messageService: MessageService
    ) {}

    ngOnInit(): void {
        this.load();
    }

    load(): void {
        const id = Number(this.route.snapshot.paramMap.get('id'));
        if (!id) {
            return;
        }
        this.chartOfAccountsService.getChartOfAccountsById(id, true).subscribe({
            next: account => {
                this.account = account;
                this.childAccountTree = (account.childrenAccounts || []).map(child => this.buildTreeNode(child));
            },
            error: err => this.messageService.add({ severity: 'error', summary: 'Load failed', detail: err?.error?.message || 'Could not load account.' })
        });
    }

    back(): void {
        this.location.back();
    }

    edit(): void {
        if (this.account?.accountId) {
            this.router.navigate(['/chart-of-accounts/edit', this.account.accountId]);
        }
    }

    addChild(): void {
        if (this.account?.accountId) {
            this.router.navigate(['/chart-of-accounts/add', this.account.accountId]);
        }
    }

    viewChild(account: ChartOfAccounts): void {
        if (account.accountId) {
            this.router.navigate(['/chart-of-accounts/view', account.accountId]).then(() => this.load());
        }
    }

    isProtectedPostingAccount(): boolean {
        return !!this.account && this.protectedPostingAccountNames.has(this.account.accountName);
    }

    accountStatus(): string {
        if (this.account?.deprecated) return 'Deprecated';
        if (this.account?.isActive === false) return 'Inactive';
        if (this.account?.isActive === null || this.account?.isActive === undefined) return 'Needs Review';
        return 'Active';
    }

    accountStatusSeverity(): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        if (this.account?.deprecated) return 'danger';
        if (this.account?.isActive === false) return 'secondary';
        if (this.account?.isActive === null || this.account?.isActive === undefined) return 'warn';
        return 'success';
    }

    accountStatusFor(account: ChartOfAccounts): string {
        if (account.deprecated) return 'Deprecated';
        if (account.isActive === false) return 'Inactive';
        if (account.isActive === null || account.isActive === undefined) return 'Needs Review';
        return 'Active';
    }

    accountStatusSeverityFor(account: ChartOfAccounts): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        if (account.deprecated) return 'danger';
        if (account.isActive === false) return 'secondary';
        if (account.isActive === null || account.isActive === undefined) return 'warn';
        return 'success';
    }

    childAccountCount(): number {
        return this.flattenChildren(this.account?.childrenAccounts || []).length;
    }

    formatBalance(account: ChartOfAccounts): string {
        const currency = account.currencyCode || this.account?.currencyCode || 'SAR';
        return `${currency} ${this.balanceMagnitude(account).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        })}`;
    }

    formatBalanceLabel(account: ChartOfAccounts): string {
        return `${this.formatBalance(account)} ${this.balanceSide(account)}`;
    }

    balanceValue(account: ChartOfAccounts): number {
        return Number(account.currentBalance ?? 0);
    }

    balanceMagnitude(account: ChartOfAccounts): number {
        return Math.abs(this.balanceValue(account));
    }

    balanceSide(account: ChartOfAccounts): string {
        const balance = this.balanceValue(account);
        if (balance > 0) return 'Dr';
        if (balance < 0) return 'Cr';
        return 'Zero';
    }

    isCreditBalance(account: ChartOfAccounts): boolean {
        return this.balanceValue(account) < 0;
    }

    isZeroBalance(account: ChartOfAccounts): boolean {
        return this.balanceValue(account) === 0;
    }

    private buildTreeNode(account: ChartOfAccounts): TreeNode<ChartOfAccounts> {
        const children = account.childrenAccounts || [];
        return {
            label: account.accountName,
            data: account,
            key: account.accountId?.toString(),
            leaf: children.length === 0,
            expanded: true,
            children: children.map(child => this.buildTreeNode(child))
        };
    }

    private flattenChildren(accounts: ChartOfAccounts[]): ChartOfAccounts[] {
        return accounts.flatMap(account => [
            account,
            ...this.flattenChildren(account.childrenAccounts || [])
        ]);
    }
}
